<template>
  <section>
    <h3 class="body-1-bold mb-16">{{ i18n(translations.priceTitle) }}</h3>

    <Graph
      class="h-64"
      data-test="graph"
      :items="props.values"
      :max-selected="selectedRange.max"
      :min-selected="selectedRange.min"
      :range="props.range"
    />

    <RevRange
      class="-mt-6 mb-16 w-full px-4 py-0 lg:px-3"
      data-test="range"
      :label="labelRange"
      :max="props.maxRange"
      :model-value="[selectedRange.min, selectedRange.max]"
      :step="props.range"
      @update:model-value="updateRange"
    />

    <div class="flex flex-row justify-between py-3">
      <RevInputText
        id="min-price"
        class="w-80"
        :format="(value) => Math.ceil(Number(value))"
        :has-clear-button="false"
        :label="
          i18n(translations.minPrice, {
            currency: i18n.currencySymbol(currency),
          })
        "
        :model-value="selectedRange.min"
        type="number"
        @update:model-value="
          (value) => updateRange([Number(value) || 0, selectedRange.max])
        "
      />

      <RevInputText
        id="max-price"
        class="w-80"
        :format="(value) => Math.ceil(Number(value))"
        :has-clear-button="false"
        :label="
          i18n(translations.maxPrice, {
            currency: i18n.currencySymbol(currency),
          })
        "
        :model-value="selectedRange.max"
        type="number"
        @update:model-value="
          (value) =>
            updateRange([selectedRange.min, Number(value) || props.maxRange])
        "
      />
    </div>

    <span class="body-2 mt-12">
      {{
        i18n(translations.avgPrice, {
          price: i18n.price(roundedUpAvgPrice),
        })
      }}
    </span>
  </section>
</template>

<script setup lang="ts">
import { computed, reactive, watch, watchEffect } from 'vue'

import type { Currency, Price } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useDebounceFn } from '@backmarket/utils/composables/useDebouncedFn'
import { clamp } from '@backmarket/utils/math/clamp'
import { RevInputText } from '@ds/components/InputText'
import { RevRange } from '@ds/components/Range'

import translations from './PriceFilter.translations'
import Graph from './components/Graph/Graph.vue'

const emits = defineEmits(['change'])
const DEBOUNCE_DELAY = 200

export type PriceFilterInputValue = { min: number; max: number }
export type PriceFilter = {
  values: number[]
  value: { min: number; max: number }
  range: number
  currency: Currency
  maxRange: number
  avgPrice: Price
}

const i18n = useI18n()
const props = defineProps<PriceFilter>()

const roundedUpAvgPrice = computed(
  (): Price => ({
    amount: Math.ceil(Number(props.avgPrice.amount)).toString(),
    currency: props.avgPrice.currency,
  }),
)

const labelRange = computed((): [string, string] => [
  i18n(translations.minPrice, { currency: props.currency }),
  i18n(translations.maxPrice, { currency: props.currency }),
])

const selectedRange = reactive({
  min: props.value.min,
  max: props.value.max,
})

watch(
  selectedRange,
  useDebounceFn(
    () => emits('change', { min: selectedRange.min, max: selectedRange.max }),
    DEBOUNCE_DELAY,
  ),
)

watchEffect(() => {
  selectedRange.min = clamp(props.value.min, 0, props.maxRange)
  selectedRange.max = clamp(props.value.max, 0, props.maxRange)
})

const updateRange = (value: number[]) => {
  const newValue = value
    .map((x) => clamp(x, 0, props.maxRange))
    .sort((a, b) => a - b)

  selectedRange.min = newValue[0]
  selectedRange.max = newValue[1]
}
</script>
